import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { useLang } from "@/context/LangContext";

const STORAGE_KEY = "flv_cookies_v1";

// Custom event so the "Manage my cookies" link in the footer can re-open this banner.
const REOPEN_EVENT = "flv:open-cookies";

export const openCookiePreferences = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(REOPEN_EVENT));
  }
};

const readChoice = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeChoice = (choice) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...choice, ts: Date.now() }),
    );
  } catch {
    /* noop */
  }
};

const CookieBanner = () => {
  const { lang } = useLang();
  const isFr = lang === "fr";
  const [open, setOpen] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!readChoice()) setOpen(true);
    const reopen = () => {
      const existing = readChoice() || {};
      setAnalytics(!!existing.analytics);
      setMarketing(!!existing.marketing);
      setCustomizing(true);
      setOpen(true);
    };
    window.addEventListener(REOPEN_EVENT, reopen);
    return () => window.removeEventListener(REOPEN_EVENT, reopen);
  }, []);

  if (!open) return null;

  const acceptAll = () => {
    writeChoice({ necessary: true, analytics: true, marketing: true });
    setOpen(false);
  };

  const rejectAll = () => {
    writeChoice({ necessary: true, analytics: false, marketing: false });
    setOpen(false);
  };

  const savePreferences = () => {
    writeChoice({ necessary: true, analytics, marketing });
    setOpen(false);
  };

  const btnBase = {
    padding: "14px 14px",
    fontSize: 11,
    letterSpacing: ".14em",
    textTransform: "uppercase",
    borderRadius: 8,
    fontFamily: "Arial,Helvetica,sans-serif",
    fontWeight: 600,
    cursor: "pointer",
    lineHeight: 1.1,
    minHeight: 48,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const btnPrimary = {
    ...btnBase,
    background: "#3D2B1F",
    color: "#FFFFFF",
    border: "1px solid #3D2B1F",
  };

  const btnSecondary = {
    ...btnBase,
    background: "#FFFFFF",
    color: "#3D2B1F",
    border: "1px solid #3D2B1F",
  };

  return (
    <div
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[60] px-3 sm:px-6 pb-3 sm:pb-6"
      role="dialog"
      aria-labelledby="cookie-title"
    >
      <div
        className="max-w-4xl mx-auto"
        style={{
          background: "#FFFFFF",
          border: "1px solid #dcd7ce",
          boxShadow: "0 20px 60px -20px rgba(61,43,31,0.35)",
          borderRadius: 12,
          padding: "16px 16px 14px 16px",
          fontFamily: "Arial,Helvetica,sans-serif",
          color: "#3D2B1F",
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div
              id="cookie-title"
              className="text-[15px] sm:text-[18px]"
              style={{ fontFamily: "Georgia,serif", color: "#1A1A1A", marginBottom: 2, lineHeight: 1.25 }}
            >
              {isFr ? "🍪 Vos préférences cookies" : "🍪 Your cookie preferences"}
            </div>
            <div className="text-[12px] sm:text-[13px]" style={{ color: "#4a4a4a", lineHeight: 1.5 }}>
              <span className="hidden sm:inline">
                {isFr
                  ? "Nous utilisons des cookies pour faire fonctionner le site (panier, langue), mesurer l'audience et personnaliser nos communications. Vous pouvez tout accepter, tout refuser, ou personnaliser. Conformément à la CNIL, refuser est aussi facile qu'accepter."
                  : "We use cookies to make the site work (cart, language), measure audience and personalize communications. You can accept all, reject all, or customize. Per CNIL guidance, refusing is as easy as accepting."}
              </span>
              <span className="sm:hidden">
                {isFr
                  ? "Panier, mesure d'audience, personnalisation. Refuser est aussi facile qu'accepter."
                  : "Cart, audience measurement, personalization. Refusing is as easy as accepting."}
              </span>
              {" "}
              <Link to="/legal/cookies" style={{ color: "#8B6343", textDecoration: "underline" }}>
                {isFr ? "En savoir plus" : "Learn more"}
              </Link>
              .
            </div>
          </div>
          {!customizing && (
            <button
              type="button"
              onClick={rejectAll}
              data-testid="cookie-close"
              aria-label={isFr ? "Fermer (tout refuser)" : "Close (reject all)"}
              className="p-2 -m-2 shrink-0"
              style={{ color: "#8a8378" }}
            >
              <X size={18} />
            </button>
          )}
        </div>

        {customizing && (
          <div className="mt-5 space-y-3" data-testid="cookie-customize">
            <label className="flex items-center gap-3" style={{ opacity: 0.6 }}>
              <input type="checkbox" checked readOnly />
              <span style={{ fontSize: 13 }}>
                <strong>{isFr ? "Nécessaires" : "Necessary"}</strong> — {isFr ? "toujours actifs (panier, session, sécurité)" : "always on (cart, session, security)"}
              </span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                data-testid="cookie-analytics"
              />
              <span style={{ fontSize: 13 }}>
                <strong>Analytics</strong> — {isFr ? "mesure d'audience anonyme" : "anonymous audience measurement"}
              </span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                data-testid="cookie-marketing"
              />
              <span style={{ fontSize: 13 }}>
                <strong>Marketing</strong> — {isFr ? "personnalisation publicitaire" : "ad personalization"}
              </span>
            </label>
          </div>
        )}

        <div className="mt-4">
          {!customizing ? (
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={acceptAll}
                data-testid="cookie-accept"
                style={{ ...btnPrimary, width: "100%" }}
              >
                {isFr ? "Tout accepter" : "Accept all"}
              </button>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={rejectAll}
                  data-testid="cookie-reject"
                  style={btnSecondary}
                >
                  {isFr ? "Tout refuser" : "Reject all"}
                </button>
                <button
                  type="button"
                  onClick={() => setCustomizing(true)}
                  data-testid="cookie-customize-btn"
                  style={btnSecondary}
                >
                  {isFr ? "Personnaliser" : "Customize"}
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => { setCustomizing(false); }}
                data-testid="cookie-back"
                style={btnSecondary}
              >
                {isFr ? "Retour" : "Back"}
              </button>
              <button
                type="button"
                onClick={savePreferences}
                data-testid="cookie-save"
                style={btnPrimary}
              >
                {isFr ? "Enregistrer" : "Save"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
